import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Alert, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useRouter } from 'expo-router';
import { useAppStore } from '../store/useAppStore';

import { firestoreService } from '../services/firestoreService';
import { callMarkSchoolTrialStart } from '../services/cloudFunctions';
import { volver } from '../utils/volver';
import { useGuardiaEscolar } from '../hooks/useGuardiaEscolar';
import { ratingDisplay } from '../services/ratingUtils';
import { pesos } from '../utils/dinero';

const DEFAULT_ZONE_FEES = { A: 3500, B: 4500, C: 5500 };

export default function SchoolDriverDirectoryScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  const students = useAppStore(state => state.students);
  const setActiveChatPartner = useAppStore(state => state.setActiveChatPartner);
  // El padre elige el schoolId (uid del director). Por defecto: el primer estudiante registrado.
  const [selectedSchool, setSelectedSchool] = useState<string>(students[0]?.school ?? '');
  const [currentDrivers, setCurrentDrivers] = useState<any[]>([]);
  // Lista dinámica de escuelas de los estudiantes registrados (más concreto que un mock estático)
  const SCHOOLS_LIST = Array.from(new Set(students.map(s => s.school).filter(Boolean)));

  useEffect(() => {
    if (!selectedSchool) {
      setCurrentDrivers([]);
      return;
    }
    const unsubscribe = firestoreService.listenToSchoolDrivers(selectedSchool, (drivers) => {
      // FIX AUDIT — Modelo de búsqueda por RUTA: mostramos a los choferes que
      // declararon servir este colegio (la query ya filtra por schoolKeys). Ya
      // NO exigimos certificación del director (antes exigía status
      // 'Certificado' → lista vacía para siempre). Cada perfil ya trae su
      // biometría verificada; el padre decide a quién contratar.
      setCurrentDrivers(drivers.filter((d: any) => d.schoolDriverListed !== false));
    });
    return () => unsubscribe();
  }, [selectedSchool]);

  const handleLinkStudent = (driverName: string) => {
    if (students.length === 0) {
      Alert.alert(
        'Inscripción Escolar',
        `Para inscribirte con ${driverName}, primero debes registrar los datos de tu hijo en tu perfil.`,
        [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Registrar Hijo', onPress: () => router.push('/students') }
        ]
      );
      return;
    }

    // Si hay estudiantes registrados, permitir elegir cuál vincular.
    // Al confirmar, arrancamos el TRIAL de 15 días del chofer (server-side).
    const buttons = students.map(student => ({
      text: student.name,
      onPress: async () => {
        try {
          // Buscar el driver real seleccionado (ya estaba en currentDrivers)
          const driver = currentDrivers.find((d: any) => d.name === driverName);
          if (driver?.id) {
            await callMarkSchoolTrialStart({ schoolDriverId: driver.id });
          }
        } catch (e) {
          console.warn('No se pudo arrancar el trial del chofer escolar:', e);
        }
        Alert.alert(
          'Inscripción Solicitada',
          `¡La solicitud de inscripción de ${student.name} ha sido enviada al chofer ${driverName} y al departamento administrativo de ${selectedSchool}!\n\nEl chofer empieza su prueba gratis de 30 días.`,
          [{ text: 'OK', onPress: () => volver(router) }]
        );
      }
    }));

    Alert.alert(
      '¿A quién deseas inscribir?',
      `Selecciona el alumno que abordará la ruta de ${driverName}:`,
      [
        ...buttons,
        { text: 'Cancelar', style: 'cancel' }
      ]
    );
  };

  // Vertical escolar oculto: ver VERTICAL_ESCOLAR_VISIBLE en featureFlags.ts
  const escolarOculto = useGuardiaEscolar();
  if (escolarOculto) return null;

  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        <View style={[styles.header, { borderBottomColor: colors.border }]}>
          <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.text }]}>Buscar Transporte Escolar</Text>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            Selecciona tu colegio para ver la lista de choferes escolares certificados y autorizados.
          </Text>

          {/* SELECTOR DE COLEGIO */}
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>SELECCIONAR COLEGIO</Text>
          <View style={styles.schoolsContainer}>
            {SCHOOLS_LIST.length === 0 ? (
              <Text style={{ color: colors.textSecondary, fontStyle: 'italic', paddingVertical: 10 }}>
                No hay colegios afiliados en tu área actualmente.
              </Text>
            ) : (
              SCHOOLS_LIST.map((school) => (
                <TouchableOpacity
                  key={school}
                  style={[
                    styles.schoolPill,
                    { borderColor: colors.border },
                    selectedSchool === school && { backgroundColor: colors.primary, borderColor: colors.primary }
                  ]}
                  onPress={() => setSelectedSchool(school)}
                >
                  <Text
                    style={[
                      styles.schoolPillText,
                      { color: colors.textSecondary },
                      selectedSchool === school && { color: '#FFF', fontWeight: 'bold' }
                    ]}
                  >
                    {school}
                  </Text>
                </TouchableOpacity>
              ))
            )}
          </View>

          {/* LISTA DE CHOFERES */}
          <Text style={[styles.sectionTitle, { color: colors.textSecondary, marginTop: 25 }]}>
            CONDUCTORES AUTORIZADOS POR LA DIRECCIÓN ({currentDrivers.length})
          </Text>

          {currentDrivers.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 40 }}>
              <Ionicons name="bus-outline" size={48} color={colors.border} />
              <Text style={{ color: colors.textSecondary, marginTop: 16, textAlign: 'center' }}>
                No hay choferes asignados a este colegio aún.
              </Text>
            </View>
          ) : (
            currentDrivers.map((driver: any) => {
              // Defensivo: campos del driver pueden venir ausentes si el director aún no
              // los completó. Caemos a defaults razonables en lugar de crashear con `undefined`.
              const zones = driver.zones ?? DEFAULT_ZONE_FEES;
              const avatarUri = driver.avatar || driver.photoURL || null;
              const vehicle = driver.vehicle ?? 'Vehículo escolar';
              const plate = driver.plate ?? '—';
              // ── NADA DE CINCO ESTRELLAS DE RELLENO ──────────────────────
              // Antes: `driver.rating ?? '5.0'`. A un padre que está eligiendo
              // quién lleva a su hijo al colegio se le enseñaban cinco
              // estrellas que no había puesto nadie. El ayudante del proyecto
              // dice "Nuevo" cuando no hay ni una nota real.
              // El contador de notas de un chofer es `driverRatingCount`, que
              // es lo que escribe functions/src/ratings.ts en /users/{uid} (el
              // campo suelto `rating` es solo la copia mostrable del promedio).
              const notas = ratingDisplay(driver.rating, driver.driverRatingCount);
              // `trips` no lo escribe NADIE en /users: no existe una función
              // que lo incremente. Poner "(0 viajes)" era afirmar que este
              // chofer no ha hecho ninguno, y eso no lo sabemos. Sin dato, no
              // se dice nada.
              const trips = typeof driver.trips === 'number' ? driver.trips : null;
              // El escudo tiene que poder decir que NO. Se pintaba siempre, así
              // que todos los choferes del directorio salían con sello verde
              // aunque nadie hubiera revisado su identidad. `isDriverVerified`
              // es el campo real de /users que usan el mapa y el perfil.
              const identidadVerificada = driver.isDriverVerified === true;
              const seats = driver.seats ?? 'Capacidad por confirmar';
              return (
              <View key={driver.id} style={[styles.driverCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.driverHeader}>
                  {avatarUri ? (
                    <Image source={{ uri: avatarUri }} style={styles.avatar} />
                  ) : (
                    <View style={[styles.avatar, { backgroundColor: '#F0F0F0', justifyContent: 'center', alignItems: 'center' }]}>
                      <Ionicons name="person" size={24} color="#999" />
                    </View>
                  )}
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <Text style={[styles.driverName, { color: colors.text }]}>{driver.name ?? 'Sin nombre'}</Text>
                      {identidadVerificada && (
                        <View style={styles.verifiedBadge}>
                          <Ionicons name="shield-checkmark" size={12} color="#00C853" />
                        </View>
                      )}
                    </View>
                    <Text style={[styles.driverVehicle, { color: colors.textSecondary }]}>
                      {vehicle} • Placa: {plate}
                    </Text>
                    <View style={styles.ratingRow}>
                      {notas.isNew ? (
                        <View style={styles.nuevoChip}>
                          <Text style={styles.nuevoChipText}>Nuevo</Text>
                        </View>
                      ) : (
                        <>
                          <Ionicons name="star" size={14} color="#FFD700" style={{ marginRight: 4 }} />
                          <Text style={[styles.ratingText, { color: colors.text }]}>{notas.label}</Text>
                        </>
                      )}
                      {trips !== null && (
                        <Text style={[styles.tripsText, { color: colors.textSecondary }]}>({trips} viajes)</Text>
                      )}
                      <View style={styles.bullet} />
                      <Text style={[styles.seatsText, { color: colors.textSecondary }]}>{seats}</Text>
                    </View>
                  </View>
                </View>

                <View style={[styles.divider, { backgroundColor: colors.border }]} />

                {/* PRECIOS POR ZONAS */}
                <Text style={[styles.pricingTitle, { color: colors.textSecondary }]}>
                  TARIFAS MENSUALES SEGÚN TU DISTANCIA AL COLEGIO:
                </Text>
                <View style={styles.pricingRow}>
                  <View style={[styles.priceBox, { backgroundColor: colors.background, borderColor: colors.border }]}>
                    <Text style={[styles.zoneLabel, { color: colors.textSecondary }]}>Zona A (0-5km)</Text>
                    <Text style={[styles.zonePrice, { color: colors.primary }]}>{pesos(zones.A ?? DEFAULT_ZONE_FEES.A)}</Text>
                  </View>
                  <View style={[styles.priceBox, { backgroundColor: colors.background, borderColor: colors.border }]}>
                    <Text style={[styles.zoneLabel, { color: colors.textSecondary }]}>Zona B (5-10km)</Text>
                    <Text style={[styles.zonePrice, { color: colors.primary }]}>{pesos(zones.B ?? DEFAULT_ZONE_FEES.B)}</Text>
                  </View>
                  <View style={[styles.priceBox, { backgroundColor: colors.background, borderColor: colors.border }]}>
                    <Text style={[styles.zoneLabel, { color: colors.textSecondary }]}>Zona C (+10km)</Text>
                    <Text style={[styles.zonePrice, { color: colors.primary }]}>{pesos(zones.C ?? DEFAULT_ZONE_FEES.C)}</Text>
                  </View>
                </View>

                {/* BADGES DE SEGURIDAD */}
                <View style={styles.securityRow}>
                  {/* ── AQUÍ TAMPOCO CABE LA PALABRA "BIOMETRÍA" ─────────────
                      Decía "Biometría Aprobada". Los Términos de la app dicen,
                      en negrita, que la selfie "la compara a mano una persona
                      autorizada de Go Safely" y que "no usamos reconocimiento
                      facial, prueba de vida ni ningún sistema automático"
                      (src/app/terms.tsx; la Política de Privacidad lo repite en
                      src/app/privacy.tsx). Además se pintaba SIEMPRE, para
                      cualquier chofer de la lista. Ahora dice lo que sí es
                      verdad y solo cuando el dato real lo respalda. */}
                  {identidadVerificada && (
                    <View style={styles.secItem}>
                      <Ionicons name="shield-checkmark" size={14} color="#00C853" />
                      <Text style={[styles.secText, { color: colors.textSecondary }]}>Identidad verificada</Text>
                    </View>
                  )}
                  <View style={styles.secItem}>
                    <Ionicons name="location" size={14} color="#00C853" />
                    <Text style={[styles.secText, { color: colors.textSecondary }]}>Ruta en el mapa</Text>
                  </View>
                  <View style={styles.secItem}>
                    <Ionicons name="snow" size={14} color="#00C853" />
                    <Text style={[styles.secText, { color: colors.textSecondary }]}>Aire A/C Ok</Text>
                  </View>
                </View>

                <View style={styles.actionButtonsContainer}>
                  <TouchableOpacity
                    style={[styles.chatBtn, { backgroundColor: 'rgba(33, 150, 243, 0.1)' }]}
                    onPress={() => {
                      // Abrimos el chat reutilizable (mismo componente que rent-car-chat)
                      setActiveChatPartner({
                        name: driver.name ?? 'Chofer Escolar',
                        vehicle: `${vehicle} • ${plate}`,
                        role: 'DRIVER',
                      });
                      router.push('/rent-car-chat');
                    }}
                  >
                    <Ionicons name="chatbubble-ellipses" size={24} color="#2196F3" />
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.linkBtn, { backgroundColor: colors.primary }]}
                    onPress={() => handleLinkStudent(driver.name ?? 'el chofer')}
                  >
                    <Text style={styles.linkBtnText}>Inscribir a mi Hijo</Text>
                  </TouchableOpacity>
                </View>
              </View>
              );
            })
          )}

          <View style={{ height: 100 }} />
        </ScrollView>
      </SafeAreaView>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
    marginLeft: -8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  container: {
    padding: 20,
    paddingTop: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    flexGrow: 1,
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 20,
    lineHeight: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 12,
    marginLeft: 4,
    letterSpacing: 1,
  },
  schoolsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  schoolPill: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
  },
  schoolPillText: {
    fontSize: 13,
    fontWeight: '600',
  },
  driverCard: {
    borderRadius: 24,
    padding: 20,
    borderWidth: 1,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 2,
  },
  driverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginRight: 16,
  },
  driverName: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  verifiedBadge: {
    backgroundColor: 'rgba(0, 200, 83, 0.1)',
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  driverVehicle: {
    fontSize: 13,
    marginTop: 2,
  },
  nuevoChip: {
    backgroundColor: 'rgba(255,149,0,0.15)',
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 6,
    marginRight: 6,
  },
  nuevoChipText: {
    fontSize: 10,
    fontWeight: '800',
    color: '#FF9500',
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  ratingText: {
    fontSize: 13,
    fontWeight: 'bold',
  },
  tripsText: {
    fontSize: 12,
    marginLeft: 4,
  },
  bullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#CCC',
    marginHorizontal: 8,
  },
  seatsText: {
    fontSize: 12,
  },
  divider: {
    height: 1,
    marginVertical: 16,
  },
  pricingTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  pricingRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  priceBox: {
    flex: 1,
    borderRadius: 12,
    padding: 10,
    borderWidth: 1,
    alignItems: 'center',
  },
  zoneLabel: {
    fontSize: 9,
    fontWeight: '600',
    marginBottom: 4,
  },
  zonePrice: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  securityRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    backgroundColor: 'rgba(0, 200, 83, 0.04)',
    padding: 10,
    borderRadius: 12,
  },
  secItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  secText: {
    fontSize: 10,
    fontWeight: '600',
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  chatBtn: {
    width: 54,
    height: 54,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  linkBtn: {
    flex: 1,
    height: 54,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  linkBtnText: {
    color: '#FFF',
    fontSize: 15,
    fontWeight: 'bold',
  }
});

